<template>
	<header ref="el" data-component="faq-header">
		<div class="g-row">
			<div class="g-col xxl-19 xxl-offset-5 xs-14 xs-offset-1 header-content">
				<h1 class="heading">
					<span v-if="title" ref="titleEl" class="-title-3 title">{{ title }}</span>
					<span v-if="subtitle" ref="subtitleEl" class="-title-3 subtitle">{{ subtitle }}</span>
				</h1>

				<p v-if="formattedUpdatedAt" ref="updatedEl" class="-label-regular updated-at">
					<span>Last Updated</span>
					<span aria-hidden="true">/</span>
					<time :datetime="updatedAt">{{ formattedUpdatedAt }}</time>
				</p>
			</div>
		</div>
	</header>
</template>
 
<script setup>
import { gsap, SplitText } from 'gsap/all'

import.meta.client && gsap.registerPlugin(SplitText)

const props = defineProps({
	title: { type: String, default: undefined },
	subtitle: { type: String, default: undefined },
	updatedAt: { type: String, default: undefined },
})

const el = ref(null)
const titleEl = ref(null)
const subtitleEl = ref(null)
const updatedEl = ref(null)
const pageContext = inject('pageContext')

let animationContext = null
let entryTimeline = null
let splitSubtitle = null
let hasAnimated = false

const formattedUpdatedAt = computed(() => {
	if (!props.updatedAt) return ''

	const date = new Date(props.updatedAt)
	if (Number.isNaN(date.getTime())) return props.updatedAt

	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
})

const setup = () => {
	if (!import.meta.client || !el.value) return

	gsap.set(titleEl.value, { y: 24, autoAlpha: 0 })
	gsap.set(subtitleEl.value, { autoAlpha: 0 })
	gsap.set(updatedEl.value, { y: 24, autoAlpha: 0 })
}

const init = async () => {
	if (!import.meta.client || !el.value || hasAnimated) return
	hasAnimated = true

	await nextTick()

	animationContext = gsap.context(() => {
		splitSubtitle = subtitleEl.value ? new SplitText(subtitleEl.value, { type: 'lines', linesClass: 'split-line' }) : null
		const subtitleLines = splitSubtitle?.lines || []

		subtitleLines.length && gsap.set(subtitleLines, { yPercent: 110, autoAlpha: 0 })
		subtitleEl.value && gsap.set(subtitleEl.value, { autoAlpha: 1 })

		entryTimeline = gsap.timeline()

		titleEl.value && entryTimeline.to(titleEl.value, { y: 0, autoAlpha: 1, duration: 0.6, ease: 'power3.out' }, 0.18)
		subtitleLines.length && entryTimeline.to(subtitleLines, { yPercent: 0, autoAlpha: 1, duration: 0.75, stagger: 0.08, ease: 'power3.out' }, 0.26)
		updatedEl.value && entryTimeline.to(updatedEl.value, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power3.out' }, 0.48)
	}, el.value)
}

const kill = () => {
	entryTimeline?.kill()
	entryTimeline = null
	splitSubtitle?.revert()
	splitSubtitle = null
	animationContext?.revert()
	animationContext = null
}

onBeforeUnmount(() => kill())
onMounted(() => {
	const loader = pageContext?.$page?.loader
	setup()

	if (!loader) {
		init()
		return
	}

	loader.ready.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='faq-header'] {
	position: relative;
	z-index: 2;
	padding-top: rs(190, 145);

	.header-content {
		display: flex;
		flex-direction: column;
	}

	.heading {
		display: flex;
		flex-direction: column;
		margin: 0;
	}

	.title,
	.subtitle {
		display: block;
		:deep(.split-line) {
			@include inherit-font;
		}
	}

	.title {
		text-indent: column-width(2, true);
		color: var(--color-river-blue-500);
	}

	.subtitle {
		max-width: column-width(10);
		color: var(--color-river-blue-900);

		:deep(.split-line) {
			display: block;
			line-height: inherit;
		}
	}

	.updated-at {
		display: flex;
		gap: rs(12, 10);
		margin-top: rs(18, 16);
		color: var(--color-river-blue-900);
		margin-left: column-width(2, true);

		span {
			@include inherit-font;
		}
		time {
			@include inherit-font;
		}
	}

	@include max-width($xs) {
		.subtitle {
			max-width: 100%;
		}
	}
}
</style>
